// Photos de pièces prises au téléphone : un capteur récent produit des JPEG de 10 Mo et plus, au-delà de
// la limite d'envoi. On les réduit dans le navigateur avant l'envoi : 2000 px sur le plus grand côté
// restent largement lisibles pour la vérification (IA comme humaine).

export const MAX_UPLOAD_BYTES = 10 * 1e6
const MAX_SIDE = 2000
const RESIZE_ABOVE_BYTES = 3 * 1e6
const JPEG_QUALITY = 0.85

/** Dimensions réduites pour que le plus grand côté ne dépasse pas `maxSide` (inchangées sinon). */
export const fitWithin = (width: number, height: number, maxSide: number = MAX_SIDE): { width: number; height: number } => {
  const longest = Math.max(width, height)
  if (longest <= maxSide) return { width, height }
  const ratio = maxSide / longest
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) }
}

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = (error) => {
      URL.revokeObjectURL(url)
      reject(error)
    }
    image.src = url
  })

/** Réduit une photo trop lourde ; rend le fichier d'origine pour un PDF, une petite image, ou en cas d'échec. */
export const downscaleImageForUpload = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/') || file.size <= RESIZE_ABOVE_BYTES) return file
  try {
    const image = await loadImage(file)
    const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(image, 0, 0, width, height)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
    if (!blob || blob.size >= file.size) return file
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified })
  } catch {
    return file
  }
}
